<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      Salesbox
    </portal>
    <template v-if="config !== null">
      <card cols="12 6@desktop">
        <hm-card-inner>
          <hm-input-toggle :disabled="!canEnableInteralpSalesbox"
                           v-model="config.enabled"
                           label="Enabled?"
                           cols="12 6@hd start-1"
                           name="enabled" />
        </hm-card-inner>
        <hm-card-inner v-if="!config.enabled" :full-size="true">
          <hm-col cols="12">
            <span class="warning-text warning-text--full-size warning-text--no-margin col-12">Interface is <b><u>DISABLED</u></b>. Reservations will <b><u>NOT</u></b> be imported. Availability changes will <b><u>NOT</u></b> be synced. Price changes will <b><u>NOT</u></b> be synced.</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input v-model="config.baseUrl"
                    :validate="'required|regex:^http[s]?\://.*[^/]$'"
                    label="Base URL"
                    help="DO NOT include the trailing slash"
                    cols="12 6@hd start-1"
                    name="baseUrl" />
          <hm-input v-model="config.hotelId"
                    label="Hotel ID"
                    cols="12 6@hd start-1"
                    name="hotelId" />
          <hm-input v-model="config.terminalId"
                    label="Terminal ID"
                    cols="12 6@hd start-1"
                    name="terminalId" />
          <hm-input v-model="config.agentDutyCode"
                    label="Agent Duty Code"
                    type="password"
                    autocomplete="interalp-salesbox-agent-duty-code"
                    cols="12 6@hd start-1"
                    name="agentDutyCode" />
          <hm-input-date v-model="config.interfaceStartDate"
                         label="First interface activation date"
                         cols="12 6@hd start-1"
                         name="interfaceStartDate" />
          <hm-input-toggle v-model="config.autoPullReservations"
                           label="Auto pull reservations?"
                           cols="12 6@hd start-1"
                           name="autoPullReservations" />
        </hm-card-inner>
      </card>
      <card title="Additional Settings" cols="12 6@desktop start-7@desktop" rows="start-1@desktop 1">
        <hm-input-toggle v-model="config.autoCloseUnmappedRoomTypes"
                         label="Auto-Close unmapped room types?"
                         cols="12 6@hd start-1"
                         name="autoCloseUnmappedRoomTypes" />
        <hm-input-select v-model="config.childAgeCategoryId"
                         label="Child Age Category ID"
                         help="Child age category to use if not child age is provided by the channel"
                         name="childAgeCategoryId"
                         cols="12 6@hd start-1">
          <option :value="null"></option>
          <option v-for="item in hotelSettings.ageCategories.all(config.childAgeCategoryId)" v-if="item.ageQualifier === 'Adult'" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
          <option v-for="item in hotelSettings.ageCategories.all(config.childAgeCategoryId)" v-if="item.ageQualifier !== 'Adult' && !item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]} (${item.ageFrom} - ${item.ageUntil})`"></option>
        </hm-input-select>
      </card>
      <card title="Room Types" cols="12 6@desktop">
        <hm-card-inner :full-size="true">
          <hm-table min-width="400px"
                    columns="2"
                    grid-template-columns="1fr 3fr">
            <template slot="table-head">
              <div class="hm-table__th">Code</div>
              <div class="hm-table__th">Name</div>
            </template>
            <template v-for="(it, index) in config.roomTypes">
              <div class="hm-table__td">
                <hm-input :lazy="true"
                          v-model="it.id"
                          :name="`code-${index+1}`"
                          :inline="true"
                          :required="true"
                          label="Code"
                          class="hm-input--inline-block" />
              </div>
              <div class="hm-table__td">
                <hm-input :lazy="true"
                          v-model="it.name"
                          :name="`name-${index+1}`"
                          :inline="true"
                          :required="true"
                          label="Name"
                          class="hm-input--inline-block" />
              </div>
            </template>
          </hm-table>
        </hm-card-inner>
      </card>
      <card title="Rate Plans" cols="12 6@desktop">
        <hm-card-inner :full-size="true">
          <hm-table min-width="400px"
                    columns="2"
                    grid-template-columns="1fr 3fr">
            <template slot="table-head">
              <div class="hm-table__th">Code</div>
              <div class="hm-table__th">Name</div>
            </template>
            <template v-for="(it, index) in config.ratePlans">
              <div class="hm-table__td">
                <hm-input :lazy="true"
                          v-model="it.id"
                          :name="`code-${index+1}`"
                          :inline="true"
                          :required="true"
                          label="Code"
                          class="hm-input--inline-block" />
              </div>
              <div class="hm-table__td">
                <hm-input :lazy="true"
                          v-model="it.name"
                          :name="`name-${index+1}`"
                          :inline="true"
                          :required="true"
                          label="Name"
                          class="hm-input--inline-block" />
              </div>
            </template>
          </hm-table>
        </hm-card-inner>
      </card>
      <card title="Mapping" cols="12 6@desktop">
        <hm-card-inner v-if="!isNil(config.roomTypes)" title="Room Type Mapping">
          <template v-for="mappingItem in config.roomTypes.filter(it => !isEmpty(it.id) && !isEmpty(it.name))">
            <hm-input-select v-model="config.roomTypeMap[mappingItem.id]"
                             :label="`${mappingItem.name} (${mappingItem.id})`"
                             :key="mappingItem.id"
                             name="roomTypeMapping"
                             cols="12 6@hd start-1">
              <option :value="null"></option>
              <option v-for="item in hotelSettings.roomTypes.all(config.roomTypeMap[mappingItem.id])" v-if="!item.deleted" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
            </hm-input-select>
          </template>
        </hm-card-inner>
        <hm-card-inner v-if="!isNil(config.ratePlans)" title="Rate Plan Mapping">
          <template v-for="mappingItem in config.ratePlans.filter(it => !isEmpty(it.id) && !isEmpty(it.name))">
            <hm-input-select v-model="config.ratePlanMap[mappingItem.id]"
                             :label="`${mappingItem.name} (${mappingItem.id})`"
                             :key="mappingItem.id"
                             name="ratePlanMapping"
                             cols="12 6@hd start-1">
              <option :value="null"></option>
              <option v-for="item in hotelSettings.ratePlans.all(config.ratePlanMap[mappingItem.id])" v-if="!item.deleted && item.ratePlanType === 'DayRate'" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
            </hm-input-select>
          </template>
        </hm-card-inner>
      </card>
      <card title="Ignore for update" cols="12 6@desktop">
        <hm-multiselect id="ingoreChannelRoomTypeIds"
                        v-model="config.ignoreChannelRoomTypeIds"
                        :options="config.roomTypes.filter(it => !isEmpty(it.id) && !isEmpty(it.name))"
                        label="Ignored room types for update"
                        cols="12 6@hd start-1"
                        name="ignoreChannelRoomTypeIds"
                        label-expression="${option.name}" />
      </card>
    </template>
    <hm-floating-buttons>
      <hm-floating-button v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                          label="Delete interface"
                          icon="delete"
                          color="red"
                          @click="deleteInterface" />
      <hm-floating-button :throttle="30 * 1000"
                          label="Retrieve Reservations"
                          icon="download"
                          color="mint"
                          @click="retrieveReservations()" />
      <hm-floating-button :throttle="120 * 1000"
                          label="Update Availability"
                          icon="upload"
                          color="orange"
                          @click="updateAvailability()" />
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import vue2Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import { cloneDeep, groupBy, isEmpty, isNil, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import Card from '../../../lib/layout/Card';
import HmInputCheckbox from '../../../lib/inputs/HmInputCheckbox';
import HmInputMoney from '../../../lib/inputs/HmInputMoney';
import HmCardInner from '../../../lib/layout/HmCardInner';
import HmTable from '../../../lib/table/HmTable';

export default {
  name: 'PageInteralpSalesbox',
  components: { HmTable, HmCardInner, HmInputMoney, HmInputCheckbox, Card },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
    {
      name: 'config',
      variable: 'config',
      fn: function (cbSaveComplete) {
        const config = cloneDeep(this.config);

        this.axios.put(this.serviceUrls.interfaces.interalpSalesbox.get(this, config.id), config)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      config: null,
      interfaceId: null,
    };
  },

  computed: {
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
    canEnableInteralpSalesbox: function () {
      if (isNil(this.config)) return true;
      return !isEmpty(this.config.roomTypes)
        && !isEmpty(this.config.ratePlans)
        && !isEmpty(this.config.hotelId)
        && !isEmpty(this.config.terminalId)
        && !isEmpty(this.config.agentDutyCode)
        && !isEmpty(this.config.baseUrl);
    },
  },

  watch: {
    canEnableInteralpSalesbox: function (newValue) {
      if (newValue === false && !isEmpty(this.config.hotelId)) {
        this.config.enabled = false;
      }
    },
    'config.roomTypes': {
      handler: function (newVal) {
        if (isNil(this.config)) return;
        if (isNil(newVal)) return;
        if (newVal.filter(it => isEmpty(it.it) && isEmpty(it.name)).length === 1 && isEmpty(newVal[newVal.length - 1].it)) return;

        let tmp = cloneDeep(newVal);
        tmp = tmp.filter(it => !isEmpty(it.it) || !isEmpty(it.name));
        tmp.push({
          id: null,
          name: null,
          minOccupancy: -1,
          maxOccupancy: -1,
        });

        this.config.roomTypes = tmp;
      },
      deep: true,
    },
    'config.ratePlans': {
      handler: function (newVal) {
        if (isNil(this.config)) return;
        if (isNil(newVal)) return;
        if (newVal.filter(it => isEmpty(it.it) && isEmpty(it.name)).length === 1 && isEmpty(newVal[newVal.length - 1].it) && isEmpty(newVal[newVal.length - 1].name)) return;

        let tmp = cloneDeep(newVal);
        tmp = tmp.filter(it => !isEmpty(it.it) || !isEmpty(it.name));
        tmp.push({
          id: null,
          name: null,
          minOccupancy: -1,
          maxOccupancy: -1,
        });

        this.config.ratePlans = tmp;
      },
      deep: true,
    },
  },

  created: function () {
    this.interfaceId = this.$route.params.interfaceId;
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    isEmpty,
    loadData() {
      this.axios.get(this.serviceUrls.interfaces.interalpSalesbox.get(this, this.interfaceId))
        .then((response) => {
          if (isNil(this.config)) {
            const config = response.data.data;

            if (isNil(config.roomTypes)) config.roomTypes = [];
            if (isNil(config.ratePlans)) config.ratePlans = [];

            config.roomTypes.push({
              id: null,
              name: null,
              minOccupancy: -1,
              maxOccupancy: -1,
            });

            config.ratePlans.push({
              id: null,
              name: null,
            });

            this.config = config;
          }
        });
    },
    updateAvailability() {
      this.axios.post(this.serviceUrls.interfaces.interalpSalesbox.updateAvailability(this, this.interfaceId))
        .then((response) => {
          this.showSuccessMessage();
        });
    },
    retrieveReservations() {
      this.axios.post(this.serviceUrls.interfaces.interalpSalesbox.retrieveReservations(this, this.interfaceId))
        .then((response) => {
          this.showSuccessMessage();
        });
    },
    deleteInterface(force = false) {
      if (force === true) {
        this.axios.delete(this.serviceUrls.interfaces.interalpSalesbox.get(this, this.interfaceId))
          .then((response) => {
            this.showSuccessMessage();
            this.$serviceBus.$emit('interface.list');
            this.$router.push({ name: 'interfaces' });
          });
      } else {
        this.showConfirmModal(
          'Are you sure, you would like to delete this Salesbox interface? This operation can not be undone.',
          (doNotShowAgain) => {
            this.deleteInterface(true);
          },
        );
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/interfaces/PageInteralpSalesbox.vue